import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * KPI 카드.
 *
 * ★ `status` 는 `lensStatusFor` 가 내는 `good|warn|bad|neutral` 과 **`unrated`**.
 *
 * ⚠️⚠️ **`unrated` 가 중요하다.** 네 축 중 셋(완주율·진행 불가·AI 오서술)은
 * **판정하지 않는다**(OBXYD 회신 25, 2026-09-20) — 기획서가 값을 주기는 하지만
 * **세는 법이 한 줄도 안 적혀 있고**, 그 값이 내 분자·분모의 값인지 알 수 없으면
 * 판정이 무의미하다. ★ 실제로 진행 불가는 46%대라 1% 기준이면 **늘 빨강**이고,
 * 늘 빨간 불은 정보가 0 이다.
 *
 * ★ 값과 변화량은 색 없이도 읽힌다. 근거가 서면 되살린다.
 */

const TONE: Record<string, { chip: string; label: string } | null> = {
  good: { chip: 'bg-[var(--lens-accent-success)] text-success', label: '정상' },
  warn: { chip: 'bg-[var(--lens-accent-warning)] text-warning', label: '경고' },
  bad: { chip: 'bg-[var(--lens-accent-error)] text-error', label: '위반' },
  // ★ 표본 없음 — 색을 입히지 않는다. 0% 를 「기준선 안」으로 읽으면 안 된다.
  neutral: null,
  // ★ 판정하지 않기로 한 축.
  unrated: null,
};

export function KpiStatCard({
  label,
  value,
  targetText,
  status,
  accent,
  trendText,
  icon: Icon,
  delay = 0,
}: {
  label: string;
  /** 축색 — ★ 4축 화면과 같은 색이라 **어느 카드가 어느 화면인지** 바로 읽힌다. */
  accent?: string | undefined;
  /** 이미 포맷된 문자열이다 — `lensFormatPct`/`lensFormatKrw` 가 만든다. */
  value: string;
  targetText: string;
  status: string;
  /** `lensTrendDelta` 가 만든 「8.3%p 개선」 꼴. 비면 안 보여준다. */
  trendText?: string;
  /** 축 아이콘(lucide). ⚠️ 이모지는 쓰지 않는다 — SUIT 에 없다. */
  icon?: LucideIcon | undefined;
  /** 등장 순서 — 네 장이 **차례로** 떠오른다. */
  delay?: number | undefined;
}) {
  const tone = TONE[status] ?? null;
  return (
    <div
      className="animate-rise relative min-w-0 overflow-hidden rounded-lg border border-border bg-card p-xl"
      style={{
        animationDelay: `${delay * 70}ms`,
        ...(accent !== undefined
          ? {
              background: `radial-gradient(130% 95% at 0% 0%, color-mix(in srgb, ${accent} 15%, transparent), transparent 62%), var(--lens-surface)`,
              boxShadow: `0 0 0 1px color-mix(in srgb, ${accent} 28%, transparent)`,
            }
          : null),
      }}
    >
      {accent !== undefined ? (
        <span
          className="animate-sweep absolute inset-x-0 top-0 h-0.5"
          style={{
            background: `linear-gradient(90deg, ${accent}, transparent 80%)`,
            animationDelay: `${delay * 70 + 140}ms`,
          }}
          aria-hidden="true"
        />
      ) : null}
      <div className="flex items-center justify-between gap-sm">
        <span className="flex min-w-0 items-center gap-sm">
          {Icon !== undefined ? (
            <Icon
              size={17}
              strokeWidth={1.9}
              aria-hidden="true"
              style={{ color: accent }}
              className="shrink-0"
            />
          ) : null}
          <span className="truncate text-title-md text-muted-foreground">{label}</span>
        </span>
        {tone !== null ? (
          <span className={cn('shrink-0 rounded-pill px-sm py-xs text-label-sm', tone.chip)}>
            {tone.label}
          </span>
        ) : null}
      </div>
      <div
        className="mt-sm text-display-md tabular-nums"
        style={accent !== undefined ? { color: accent } : undefined}
      >
        {value}
      </div>
      <div className="mt-xs text-label-md text-muted-foreground">{targetText}</div>
      {trendText !== undefined && trendText !== '' ? (
        <div className="mt-sm text-label-md text-muted-foreground">7일 {trendText}</div>
      ) : null}
    </div>
  );
}
